import React, { useState } from 'react';

import { Button, IconButton } from '@mui/material';
import styled from 'styled-components';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddIcon from '@mui/icons-material/Add';
import RedoIcon from '@mui/icons-material/Redo';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import UndoIcon from '@mui/icons-material/Undo';
import WarehouseIcon from '@mui/icons-material/Warehouse';

import CorporationCard from './CorporationCard';
import GameHeader from './GameHeader';

const tfButtonStyles = {
  height: '100%',
  fontSize: '2rem',
};

const Cart = ({
  adjustTerraformRating,
  cartActive,
  corporation,
  executePurchase,
  future,
  gameHeaderProps,
  gameState,
  history,
  redo,
  resourcePhase,
  showTutorial,
  toggleCart,
  tutorialStepIndex,
  undo,
}) => {
  const [cost, setCost] = useState({ money: 0, steel: 0, titanium: 0 });

  const adjustResource = (resource, amount) => {
    const newCost = { ...cost };
    let newResourceAmount = Math.max(newCost[resource] + amount, 0);
    newResourceAmount = Math.min(newResourceAmount, gameState[resource].amount);

    newCost[resource] = newResourceAmount;
    setCost(newCost);
  };

  const handlePurchase = () => {
    executePurchase(gameState, cost);
    setCost({ money: 0, steel: 0, titanium: 0 });
  };

  const { money, steel, titanium } = cost;
  const titaniumFactor = corporation.name === 'Phobolog' ? 4 : 3;
  const total = money + steel * 2 + titanium * titaniumFactor;

  const boldSx = { fontWeight: 700 };

  const showCart = cartActive || tutorialStepIndex > 7;

  return (
    <Container>
      <GameHeader {...gameHeaderProps} />
      <LowerSection>
        <TerraformRating id="TerraformRating">
          <IconButton
            variant="outlined"
            size="large"
            color="primary"
            onClick={() => adjustTerraformRating(-1)}
            sx={tfButtonStyles}
          >
            <RemoveCircleIcon size="large" />
          </IconButton>
          <h1 className="tf-title">Terraform Rating:</h1>
          <h1 className="tf-value">{gameState.terraformRating}</h1>
          <IconButton
            variant="outlined"
            size="large"
            color="primary"
            onClick={() => adjustTerraformRating(1)}
            sx={tfButtonStyles}
          >
            <AddCircleIcon size="large" />
          </IconButton>
        </TerraformRating>
        <Button
          id="ResourcePhaseButton"
          variant="contained"
          onClick={resourcePhase}
          sx={boldSx}
          startIcon={<WarehouseIcon />}
        >
          Execute Resource Phase
        </Button>
        <CartArea>
          {showCart ? (
            <Resources id="PurchaseResourceArea">
              <ResourceWrapper className="money">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => adjustResource('money', -1)}
                >
                  <RemoveIcon fontSize="large" />
                </Button>
                <p className="resource-adjustment-bar">
                  <span className="label">
                    {gameState.money.symbol} Mega Credits:
                  </span>
                  <span className="amount">{cost.money}</span>
                </p>
                <Button variant="contained" color="primary">
                  <AddIcon
                    fontSize="large"
                    onClick={() => adjustResource('money', 1)}
                  />
                </Button>
              </ResourceWrapper>
              <ResourceWrapper className="steel">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => adjustResource('steel', -1)}
                >
                  <RemoveIcon fontSize="large" />
                </Button>
                <p className="resource-adjustment-bar">
                  <span className="label">{gameState.steel.symbol} Steel:</span>
                  <span className="amount">{cost.steel}</span>
                </p>
                <Button variant="contained" color="primary">
                  <AddIcon
                    fontSize="large"
                    onClick={() => adjustResource('steel', 1)}
                  />
                </Button>
              </ResourceWrapper>
              <ResourceWrapper className="titanium">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => adjustResource('titanium', -1)}
                >
                  <RemoveIcon fontSize="large" />
                </Button>
                <p className="resource-adjustment-bar">
                  <span className="label">
                    {gameState.titanium.symbol} Titanium:
                  </span>
                  <span className="amount">{cost.titanium}</span>
                </p>
                <Button variant="contained" color="primary">
                  <AddIcon
                    fontSize="large"
                    onClick={() => adjustResource('titanium', 1)}
                  />
                </Button>
              </ResourceWrapper>
              <Total>Total MC: {total}</Total>
              <p>*The above will be deducted from your resources.</p>
            </Resources>
          ) : null}
          <CartButtonBar
            id="CartButtonBar"
            className={cartActive ? 'expanded' : ''}
          >
            {cartActive ? (
              <Button
                id="FinalizePurchaseButton"
                color="primary"
                size="large"
                variant="contained"
                onClick={handlePurchase}
                sx={boldSx}
                startIcon={<ShoppingCartIcon />}
              >
                Finalize Purchase
              </Button>
            ) : null}
            <Button
              variant={cartActive ? 'outlined' : 'contained'}
              onClick={toggleCart}
              size="large"
              color={cartActive ? 'error' : 'secondary'}
              sx={boldSx}
              startIcon={
                cartActive ? (
                  <RemoveShoppingCartIcon />
                ) : (
                  <AddShoppingCartIcon />
                )
              }
            >
              {cartActive ? 'Cancel Purchase' : 'Start Purchase'}
            </Button>
          </CartButtonBar>
        </CartArea>
        <GameControl id="GameControl" className="game-control">
          <Button
            color="error"
            variant="contained"
            disabled={history.length < 2 && !showTutorial}
            onClick={undo}
            startIcon={<UndoIcon />}
          >
            Undo
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={future.length < 1 && !showTutorial}
            onClick={redo}
            startIcon={<RedoIcon />}
          >
            Redo
          </Button>
        </GameControl>
        <CorporationCard corporation={corporation} />
      </LowerSection>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #000;
`;

const LowerSection = styled.div`
  padding: 0.75rem;
  display: grid;
  gap: 1rem;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;
`;

const TerraformRating = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  h1 {
    color: #ff3f3f;
    font-size: 1.7rem;
    padding: 0 4%;
  }
  .tf-value {
    font-size: 2.7rem;
  }
`;

const CartArea = styled.div`
  display: grid;
  gap: 8px;
  place-items: center;
`;

const CartButtonBar = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;

  &.expanded {
    grid-template-columns: 1fr 1fr;
  }
`;
const Resources = styled.div`
  width: 100%;
  display: grid;
  gap: 8px;
`;

const ResourceWrapper = styled.div`
  font-weight: 700;
  font-size: 1.4rem;
  place-content: center;
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;

  .resource-adjustment-bar {
    display: grid;
    grid-template-columns: 1fr 40px;
    gap: 16px;
    place-items: center;
  }

  .label,
  .amount {
    display: inline-block;
    width: 100%;
  }
  .label {
    text-align: right;
    justify-self: end;
  }
  .amount {
    text-align: left;
    justify-self: end;
  }

  &.money p {
    color: yellow;
  }
  &.steel p {
    color: #aaa;
  }
  &.titanium p {
    color: #fff;
  }
`;

const Total = styled.div`
  padding: 4px;
  text-align: center;
  border: 1px solid #fff;
  border-radius: 8px;
  font-size: 1.4rem;
  font-weight: 700;
  color: yellow;
`;

const GameControl = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default Cart;
